import React, { useEffect, useState } from 'react';
import TrackerLayout from '../../components/TrackerLayout';
import { getEODReports, deleteEODReport, type EodReportItem } from '../../lib/api';

const MOOD_META: Record<string, { emoji: string; color: string }> = {
  Great: { emoji: '🚀', color: '#10B981' },
  Good:  { emoji: '😊', color: '#3B82F6' },
  Okay:  { emoji: '😐', color: '#F0B429' },
  Tough: { emoji: '😓', color: '#EF4444' },
};

const AdminEodReports: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [reports, setReports]       = useState<(EodReportItem & { _id?: string })[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = async (date?: string) => {
    setLoading(true);
    setError('');
    try {
      const res = await getEODReports(date ? { date } : undefined);
      setReports(res.reports as any);
    } catch (e: any) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDate = (val: string) => { setFilterDate(val); load(val || undefined); };

  const handleDelete = async (r: any) => {
    const id = r._id;
    if (!id) return;
    if (!window.confirm(`Delete EOD report for ${r.salesPersonName} on ${r.date}?`)) return;
    setDeletingId(id);
    try {
      await deleteEODReport(id);
      setReports(prev => prev.filter((x: any) => x._id !== id));
    } catch (e: any) {
      alert(e.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="rounded-2xl overflow-hidden shadow-sm"
          style={{ background:'linear-gradient(135deg,#1e1730 0%,#2b2240 55%,#3d2f5e 100%)' }}>
          <div className="px-6 py-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">📋 EOD Reports</h1>
                <p className="text-sm mt-1" style={{ color:'rgba(255,255,255,.5)' }}>
                  {reports.length} report{reports.length !== 1 ? 's' : ''}{filterDate ? ` · ${filterDate}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="date" value={filterDate} max={today}
                  onChange={e => handleDate(e.target.value)}
                  className="text-sm rounded-xl px-3 py-2 focus:outline-none"
                  style={{ color:'#312741', background:'rgba(255,255,255,.9)', border:'none' }}
                />
                {filterDate && (
                  <button onClick={() => handleDate('')}
                    className="text-xs px-3 py-2 rounded-xl font-semibold"
                    style={{ background:'rgba(255,255,255,.15)', color:'rgba(255,255,255,.8)' }}>
                    Clear
                  </button>
                )}
                <button onClick={() => handleDate(today)}
                  className="text-xs px-3 py-2 rounded-xl font-bold"
                  style={{ background: filterDate === today ? '#F0B429' : 'rgba(255,255,255,.15)',
                    color: filterDate === today ? '#1a1a1a' : 'rgba(255,255,255,.8)' }}>
                  Today
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-2xl h-28 animate-pulse shadow-sm" />)}
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-5 py-4 rounded-2xl">
            {error}{' '}
            <button onClick={() => load(filterDate || undefined)} className="underline font-semibold">Retry</button>
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="text-6xl mb-3">📋</div>
            <p className="font-semibold">{filterDate ? `No EOD reports for ${filterDate}` : 'No EOD reports yet'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((r: any, i) => {
              const mood = MOOD_META[r.mood] || MOOD_META.Good;
              const isDeleting = deletingId === r._id;
              return (
                <div key={r._id || i} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  {/* Header row */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                        style={{ background:`${mood.color}18` }}>
                        {mood.emoji}
                      </div>
                      <div>
                        <p className="font-black text-gray-800">{r.salesPersonName}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{r.salesPersonId} · {r.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {r.auto ? (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600" title="They didn't send one: made at the end of the day from the CRM">
                          Not sent · made automatically
                        </span>
                      ) : (
                        <span className="text-xs font-bold px-2.5 py-1 rounded-lg"
                          style={{ background:`${mood.color}18`, color:mood.color }}>
                          {r.mood}
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(r)}
                        disabled={isDeleting}
                        className="text-xs px-3 py-1.5 rounded-lg font-semibold transition"
                        style={{ background: isDeleting ? '#f3f4f6' : '#fee2e2', color: isDeleting ? '#9ca3af' : '#dc2626', border:'1px solid #fca5a5' }}
                      >
                        {isDeleting ? '…' : '🗑 Delete'}
                      </button>
                    </div>
                  </div>

                  {/* The day's calls from the sales CRM */}
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {[
                      ['Calls', r.crm?.calls ?? r.callsMade], ['Brochures sent', r.crm?.pitched ?? r.demosGiven], ['Polls sent', r.crm ? r.crm.pollsSent ?? 0 : undefined],
                      ['Interested', r.crm ? r.crm.interested ?? 0 : undefined], ['Not interested', r.crm?.notInterested], ['Converted', r.crm?.converted ?? r.conversions],
                    ].map(([label, n]) => (
                      <div key={label} className="rounded-xl bg-gray-50 py-2 text-center">
                        <p className="text-lg font-black text-gray-800">{n ?? '—'}</p>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
                      </div>
                    ))}
                  </div>
                  {r.crm && (
                    <p className="text-xs text-gray-400 mb-3">
                      Talked to {r.crm.talked} · Not picked {r.crm.notPicked} · Call later {r.crm.callLater} · Cold {r.crm.cold} · No response {r.crm.noResponse}
                    </p>
                  )}

                  {/* Info blocks */}
                  <div className="space-y-2">
                    {[
                      { title:'🌟 End of Day',          content:r.highlights, color:'#10B981' },
                      { title:'⚠️ Challenges',           content:r.challenges, color:'#F97316' },
                      { title:'📋 Plan for Tomorrow',    content:r.plan,       color:'#3B82F6' },
                    ].filter(b => b.content).map(b => (
                      <div key={b.title} className="rounded-xl p-3"
                        style={{ borderLeft:`3px solid ${b.color}`, background:`${b.color}08` }}>
                        <p className="text-xs font-bold mb-1" style={{ color:b.color }}>{b.title}</p>
                        <p className="text-sm text-gray-600 leading-relaxed">{b.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default AdminEodReports;
